import "antd/dist/antd.css";
import "./globals.css";

import { MsalProvider } from "@azure/msal-react";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { notification } from "antd";
import { message } from "antd";
import * as React from "react";
import { render } from "react-dom";
import { Provider } from "react-redux";
import { Route, Router, Switch } from "react-router-dom";
import { PreloadedState } from "redux";
import { ThemeProvider } from "styled-components";

import { DEPRECATED_ApiManager, initApiClients, initQueryClient } from "./api";
import { TooltipGlobalStyle } from "./components";
import { ZendeskProvider } from "./contexts/zendeskContext";
import { useClientFlagsQuery } from "./hooks/useClientFlagsQuery";
import { initialAppState } from "./reducers/app";
import { AuthGuard } from "./routes/AuthGuard";
import { LoadingRoute } from "./routes/LoadingRoute";
import { OauthAttemptRedirect } from "./routes/OauthAttemptRedirect";
import Root from "./routes/Root";
import { StatsigAsyncProvider } from "./routes/StatsigAsyncProvider";
import { StatsigSynchronousProvider } from "./routes/StatsigSynchronousProvider";
import { useDashboardViewportMeta } from "./routes/WebDashboardViewer/useDashboardViewportMeta";
import { WebDashboardAccessGate } from "./routes/WebDashboardViewer/WebDashboardAccessGate";
import { WebDashboardSlackViewer } from "./routes/WebDashboardViewer/WebDashboardSlackViewer";
import { ZendeskChat } from "./routes/ZendeskChat";
import { configureStore, history, AppState } from "./store";
import {
  BroadcastChannelName,
  GoogleApi,
  errorToAuthErrorMessage,
  initPublicClientApp,
  theme,
  track,
} from "./utils";
import { ApiContext, GoogleApiContext, TrackContext } from "./utils/contexts";
import { initSentry } from "./utils/sentry";

const DashboardCrashFallback: React.FC = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      gap: 16,
      textAlign: "center",
      padding: "0 24px",
      fontFamily: "system-ui, -apple-system, sans-serif",
    }}
  >
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#f87171" strokeWidth="2" />
      <line x1="12" y1="8" x2="12" y2="13" stroke="#f87171" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="16.5" r="1.2" fill="#f87171" />
    </svg>
    <h1 style={{ fontSize: 20, fontWeight: 600, color: "#111827", margin: 0 }}>
      Something went wrong
    </h1>
    <p style={{ fontSize: 14, color: "#6b7280", maxWidth: 400, margin: 0 }}>
      The dashboard encountered an unexpected error. Please try refreshing the page.
    </p>
    <button
      onClick={() => window.location.reload()}
      style={{
        padding: "8px 20px",
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 600,
        backgroundColor: "#2563eb",
        color: "#fff",
        border: "none",
        cursor: "pointer",
      }}
    >
      Refresh Page
    </button>
  </div>
);

const WebDashboardViewerWithStatsig: React.FC = () => {
  // Must be applied here rather than inside WebDashboardViewer: the loading/error states below
  // render before the viewer mounts, and without it a phone lays them out against the 980px
  // fallback viewport (pushing LoadingRoute's centred spinner off toward the bottom-right).
  useDashboardViewportMeta();

  const { data: clientFlags, isLoading, isFetched } = useClientFlagsQuery();

  // Only block on the very first fetch. For unauthenticated viewers getClientFlags 401s
  // with no data, and any later refetch flips the query back to `loading` (rq v4), which
  // would unmount and fully reload the dashboard on every tab refocus.
  if (isLoading && !isFetched) {
    return <LoadingRoute />;
  }

  if (clientFlags) {
    return (
      <Sentry.ErrorBoundary fallback={<DashboardCrashFallback />}>
        <StatsigSynchronousProvider clientFlags={clientFlags}>
          <WebDashboardAccessGate />
        </StatsigSynchronousProvider>
      </Sentry.ErrorBoundary>
    );
  }

  // Public/unauth dashboards may 401 on getClientFlags; fall back to async Statsig initialization.
  return (
    <Sentry.ErrorBoundary fallback={<DashboardCrashFallback />}>
      <StatsigAsyncProvider>
        <WebDashboardAccessGate />
      </StatsigAsyncProvider>
    </Sentry.ErrorBoundary>
  );
};

const shouldDisableSentryFromSearch = (search: string): boolean => {
  const value = new URLSearchParams(search).get("disable_sentry");
  return value === "1" || value?.toLowerCase() === "true";
};

const init = async () => {
  const publicClientApp = await initPublicClientApp();

  const queryClient = initQueryClient();

  const urlParams = new URLSearchParams(window.location.search);

  const urlAuthParams = {
    coeffAuthToken: urlParams.get("COEFF-AUTH-TOKEN") ?? "",
    msOauthToken: urlParams.get("MICROSOFT-OAUTH-TOKEN") ?? "",
    openIdToken: urlParams.get("GOOGLE-OPENID-TOKEN") ?? "",
    oauthToken: urlParams.get("GOOGLE-OAUTH-TOKEN") ?? "",
    upgradeToken: urlParams.get("ut") ?? "",
    domain: urlParams.get("domain") ?? "",
  };

  const preloadedState: PreloadedState<AppState> = {
    app: {
      ...initialAppState,
      ...urlAuthParams,
    },
  };

  const DEPRECATED_api = new DEPRECATED_ApiManager({
    publicClientApp,
    authParams: urlAuthParams,
    onUnauthorizedError: error => {
      authBroadcastChannel.postMessage(errorToAuthErrorMessage(error));
    },
  });

  const store = configureStore(preloadedState, { api: DEPRECATED_api, history });

  const authBroadcastChannel = new BroadcastChannel(BroadcastChannelName.AUTH);

  const apiClients = initApiClients({
    publicClientApp,
    store,
    onUnauthorizedError: error => {
      authBroadcastChannel.postMessage(errorToAuthErrorMessage(error));
    },
  });

  // Allow explicit URL-based Sentry opt-out for automation/headless and other
  // flows that should not produce telemetry noise.
  if (
    !shouldDisableSentryFromSearch(window.location.search) &&
    window.location.pathname !== "/zendesk-chat"
  ) {
    initSentry();
  }

  notification.config({
    placement: "bottomRight",
    duration: 5,
  });

  message.config({
    maxCount: 1,
  });

  render(
    <MsalProvider instance={publicClientApp}>
      <ApiContext.Provider value={{ DEPRECATED_api, ...apiClients }}>
        <Router history={history}>
          <Switch>
            {/* Screenshot route with no-op tracking and minimal providers */}
            <Route path="/dashboards/:dashboardId/slack-preview">
              <TrackContext.Provider value={() => Promise.resolve()}>
                <QueryClientProvider client={queryClient}>
                  <WebDashboardSlackViewer />
                </QueryClientProvider>
              </TrackContext.Provider>
            </Route>

            {/* Standalone Zendesk chat window — no Redux, no Sentry, no Google API */}
            <Route exact path="/zendesk-chat">
              <ZendeskChat />
            </Route>

            {/* All other routes with full provider stack */}
            <Route path="*">
              <TrackContext.Provider
                value={(eventName, event) => track(DEPRECATED_api, eventName, event)}
              >
                <GoogleApiContext.Provider value={{ googleApiClient: new GoogleApi() }}>
                  <QueryClientProvider client={queryClient}>
                    <Provider store={store}>
                      <ZendeskProvider>
                        <ThemeProvider theme={theme}>
                          <TooltipGlobalStyle />
                          <Switch>
                            <Route exact path="/oauth_attempt" component={OauthAttemptRedirect} />
                            <Route
                              path="/dashboards/:dashboardId"
                              component={WebDashboardViewerWithStatsig}
                            />
                            <Route
                              path="*"
                              render={() => (
                                <AuthGuard>
                                  <Root />
                                </AuthGuard>
                              )}
                            />
                          </Switch>
                        </ThemeProvider>
                      </ZendeskProvider>
                    </Provider>
                  </QueryClientProvider>
                </GoogleApiContext.Provider>
              </TrackContext.Provider>
            </Route>
          </Switch>
        </Router>
      </ApiContext.Provider>
    </MsalProvider>,
    document.getElementById("root")
  );
};

init();
